"use client"

import { useState } from "react"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { FIELD_UNDERLINE, FieldShell } from "@/components/graphite/field"
import { PageAction } from "@/components/graphite/page-header"
import { QuietAction, TonePill } from "@/components/graphite/surface"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { stageHex } from "@/lib/stage-colors"
import { cn } from "@/lib/utils"
import { Building, Check } from "lucide-react"
import { Job } from "openapi/api/model"
//import type { Job } from "@/hooks/use-job-assignments"

interface JobSelectorDialogProps {
  isOpen: boolean
  onClose: () => void
  onAssign: (jobId: string, stage: string) => void
  availableJobs: Job[]
  selectedCount: number
}

const PIPELINE_STAGES = [
  { value: "longlist", label: "Longlist" },
  { value: "shortlist", label: "Shortlist" },
  { value: "outbound", label: "Outbound" },
  { value: "screening", label: "Screening" },
]

export function JobSelectorDialog({ isOpen, onClose, onAssign, availableJobs, selectedCount }: JobSelectorDialogProps) {
  const [selectedJobId, setSelectedJobId] = useState<string>("")
  const [selectedStage, setSelectedStage] = useState<string>("longlist")

  const handleJobSelection = (jobId: string) => {
    setSelectedJobId(jobId)
  }

  const handleStageSelection = (stage: string) => {
    setSelectedStage(stage)
  }

  const handleAssign = () => {
    if (selectedJobId) {
      onAssign(selectedJobId, selectedStage)
      onClose()
      setSelectedJobId("")
      setSelectedStage("longlist")
    }
  }

  const empty = !availableJobs || availableJobs.length === 0

  return (
    <DialogShell
      open={isOpen}
      onOpenChange={onClose}
      title="Add Candidates to Job"
      description={`Pick the job and the stage ${selectedCount} selected candidate${
        selectedCount === 1 ? "" : "s"
      } should land on.`}
      footer={
        <>
          <QuietAction size="md" onClick={onClose}>
            Cancel
          </QuietAction>
          <PageAction tone="primary" onClick={handleAssign} disabled={!selectedJobId}>
            Add to Job
          </PageAction>
        </>
      }
    >
      {empty ? (
        <p className="text-[12.5px] leading-[1.5] text-muted-foreground">
          There are no other jobs to add them to.
        </p>
      ) : (
        <>
          {/*
            Rows on one surface, not a radio beside a bordered box each. The
            row is the control — the whole of it is the click target, and the
            tick on the right is what says which one is chosen.
          */}
          <div className="text-[9.5px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
            Job
          </div>
          <div
            role="radiogroup"
            aria-label="Job"
            className="mt-[9px] divide-y border-b border-t"
          >
            {availableJobs?.map((job) => {
              const chosen = selectedJobId === job.id
              return (
                <button
                  key={job.id}
                  type="button"
                  role="radio"
                  aria-checked={chosen}
                  onClick={() => handleJobSelection(job.id)}
                  className="flex w-full items-center gap-3 py-2.5 text-left transition-colors hover:bg-foreground/[0.03]"
                >
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-[12.5px] font-medium leading-[1.3]">
                        {job.title}
                      </span>
                      {job.status && (
                        <TonePill hex={stageHex("longlist")} compact dot={false}>
                          {job.status}
                        </TonePill>
                      )}
                    </span>
                    {job.department && (
                      <span className="mt-1 flex items-center gap-1.5 text-[11.5px] leading-[1.4] text-muted-foreground">
                        <Building className="size-3 flex-none" />
                        {job.department}
                      </span>
                    )}
                  </span>
                  <span
                    className={cn(
                      "flex size-5 flex-none items-center justify-center rounded-full border transition-colors",
                      chosen
                        ? "border-transparent bg-primary text-primary-foreground"
                        : "text-transparent",
                    )}
                  >
                    <Check className="size-3" />
                  </span>
                </button>
              )
            })}
          </div>

          <FieldShell label="Pipeline stage" className="mt-5">
            <Select value={selectedStage} onValueChange={handleStageSelection}>
              <SelectTrigger
                aria-label="Pipeline stage"
                className={cn(FIELD_UNDERLINE, "h-auto pb-[7px] text-[12.5px] leading-[1.4]")}
              >
                <SelectValue placeholder="Select pipeline stage" />
              </SelectTrigger>
              <SelectContent>
                {PIPELINE_STAGES.map((stage) => (
                  <SelectItem key={stage.value} value={stage.value}>
                    {stage.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldShell>
        </>
      )}
    </DialogShell>
  )
}
